/* eslint-disable functional/prefer-readonly-type */
import React, { useEffect, useState } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core';
import {
  Downgraded,
  useHookstate,
  type State,
  type StateMethods,
} from '@hookstate/core'
import {
  DataGrid,
  GridActionsCellItem,
  gridClasses,
  type GridColDef,
  type GridInputRowSelectionModel,
  type GridSlots,
} from '@mui/x-data-grid'
import { useLang } from '~/hooks/useLang'
import useDeviceTableState, {
  type IDeviceRowsTable,
} from '../../stores/DeviceTableState'
import { ptBR } from '@mui/x-data-grid/locales'
import { Button, Chip, keyframes, LinearProgress, Tooltip } from '@mui/material'
import NoRowsOverlay from '~/components/NoRowsOverlay'
import useSystemState from '~/features/System/stores/SystemState'
import { dateTimeFormat, elapsedTime } from '~/utils/dateTime'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import useListDevicesSocket from '../../stores/ListDevicesSocketState'
import MenuList from '../MenuList'
import ConectDispatchVehicle from '~/features/Device/TabListDevice/components/ConectDispatchVehicle'
import { DeviceTransferDialog } from '~/features/Device/TabListDevice/components/DeviceTransferDialog'
import DisplayDispatchVehicle from '~/features/Device/TabListDevice/components/DisplayDispatchDevice'
import ChronologyDeviceLastDispatchDialog from '~/features/Device/TabListDevice/components/ChronologyDeviceLastDispatchButton/components/ChronologyDeviceLastDispatchDialog'
import OutOfServiceDispatchVehicle from '~/features/Device/TabListDevice/components/OutOfServiceDispatchDevice'
import ReturnToServiceDispatchVehicle from '~/features/Device/TabListDevice/components/ReturnToServiceDispatchDevice'
import DisconnectDispatchVehicle from '~/features/Device/TabListDevice/components/DisconnectDispatchDevice'
import notify from '~/utils/notify'
import useDispatchState from '~/features/Dispatch/stores/DispatchState'
import OnMissionDevice from '../OnMissionDevice'
import BackToAvailable from '../BackToAvailable'
import type { IListDeviceSocket } from '../../interfaces/IListDevicesprops'
import { useTheme } from '~/hooks/useTheme'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

interface DeviceTableUpdateSocketProps {
  readonly typeStatus: string
  readonly loading: State<boolean>
  readonly exceptColumns?: readonly string[]
  readonly searchTask: StateMethods<string>
  readonly searchDevice: StateMethods<string>
  readonly fullList?: boolean
  readonly enableCheckbox?: boolean
  readonly rowsSelected?: State<readonly IListDeviceSocket[]>
  readonly limit: StateMethods<number>
  readonly page: StateMethods<any>
  readonly rowsCount: number
}

// eslint-disable-next-line max-lines-per-function
export default function DeviceTableUpdateSocket({
  typeStatus,
  loading,
  exceptColumns = [],
  searchDevice,
  searchTask,
  fullList = false,
  enableCheckbox = false,
  rowsSelected,
}: DeviceTableUpdateSocketProps) {
  const { translate } = useLang()
  const { syncDispatchFromId } = useDispatchState()
  const {
    deviceRowsTableState,
    deviceRowsTableStateFiltered,
    triggerNewSocketState,
  } = useDeviceTableState()
  const { getStatusColors, getDeviceStatusColors } = useSystemState()
  const {
    handleMenuActions,
    returnToServiceDevice,
    disconnectDevice,
    listDevicesState,
    setBackToAvailable,
  } = useListDevicesSocket()
  const {
    openConectDispatchDevice,
    openTransferDevice,
    openCrewDialog,
    openOutOfServiceDialog,
    openReturnToServiceDialog,
    openDisconnectDialog,
    isOpenDialog,
    lockTemporaryTracking,
    deviceId,
    agencyId,
    identification,
    plate,
    canEditCrew,
    labelCommander,
    labelDriver,
    labelPatrolmans,
    labelKmInitial,
    openChronologyDevice,
    dispatchGroup,
    openBackToAvailable,
    openOnMission,
    trackingId,
  } = listDevicesState()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [rowSelectedModel, setRowSelectedModel] =
    useState<GridInputRowSelectionModel>([])
  const { darkMode } = useTheme()

  const open = Boolean(anchorEl)
  const id = open ? 'menu-appbar' : ''
  const markersFreeze = useHookstate<IDeviceRowsTable>({
    available: [],
    dispatched: [],
    disconnected: [],
    'not-in-service': [],
    'on-mission': [],
  })

  useEffect(() => {
    const identificationSearch = searchDevice.get().toLocaleLowerCase()
    const taskSearch = searchTask.get().toLocaleLowerCase()

    markersFreeze.set(deviceRowsTableState().attach(Downgraded).value)
    const freezeMap = setInterval(function () {
      markersFreeze.set(deviceRowsTableState().attach(Downgraded).value)
    }, 1000 * 5)

    const allDevices = markersFreeze
      .get()
    ['available'].concat(markersFreeze.get()['dispatched'])
      .concat(markersFreeze.get()['disconnected'])
      .concat(markersFreeze.get()['not-in-service'])
      .concat(markersFreeze.get()['on-mission'])

    if (searchDevice?.get().length > 2 && !searchTask.get()) {
      const devicesFiltered = JSON.parse(
        JSON.stringify(
          allDevices.filter((device) =>
            device.identification
              ?.toLocaleLowerCase()
              .includes(identificationSearch)
          )
        )
      )

      deviceRowsTableStateFiltered().set({
        available: JSON.parse(
          JSON.stringify(devicesFiltered.filter((row) => row['statusId'] == 1))
        ),
        dispatched: JSON.parse(
          JSON.stringify(
            devicesFiltered.filter(
              (row) => row['statusId'] >= 4 && row['statusId'] <= 9
            )
          )
        ),
        disconnected: JSON.parse(
          JSON.stringify(devicesFiltered.filter((row) => row['statusId'] == 10))
        ),
        'not-in-service': JSON.parse(
          JSON.stringify(devicesFiltered.filter((row) => row['statusId'] == 11))
        ),
        'on-mission': JSON.parse(
          JSON.stringify(devicesFiltered.filter((row) => row['statusId'] == 12))
        ),
      })
    } else if (searchTask?.get().length > 2 && !searchDevice.get()) {
      const devicesFiltered = JSON.parse(
        JSON.stringify(
          allDevices.filter((device) =>
            device.task?.toLocaleLowerCase().includes(taskSearch)
          )
        )
      )

      deviceRowsTableStateFiltered().set({
        available: JSON.parse(
          JSON.stringify(devicesFiltered.filter((row) => row['statusId'] == 1))
        ),
        dispatched: JSON.parse(
          JSON.stringify(
            devicesFiltered.filter(
              (row) => row['statusId'] >= 4 && row['statusId'] <= 9
            )
          )
        ),
        disconnected: JSON.parse(
          JSON.stringify(devicesFiltered.filter((row) => row['statusId'] == 10))
        ),
        'not-in-service': JSON.parse(
          JSON.stringify(devicesFiltered.filter((row) => row['statusId'] == 11))
        ),
        'on-mission': JSON.parse(
          JSON.stringify(devicesFiltered.filter((row) => row['statusId'] == 12))
        ),
      })
    } else if (searchTask.get().length > 2 && searchDevice.get().length > 2) {
      const devicesFiltered = JSON.parse(
        JSON.stringify(
          allDevices.filter(
            (device) =>
              device.task?.toLocaleLowerCase().includes(taskSearch) &&
              device.identification
                ?.toLocaleLowerCase()
                .includes(identificationSearch)
          )
        )
      )

      deviceRowsTableStateFiltered().set({
        available: JSON.parse(
          JSON.stringify(devicesFiltered.filter((row) => row['statusId'] == 1))
        ),
        dispatched: JSON.parse(
          JSON.stringify(
            devicesFiltered.filter(
              (row) => row['statusId'] >= 4 && row['statusId'] <= 9
            )
          )
        ),
        disconnected: JSON.parse(
          JSON.stringify(devicesFiltered.filter((row) => row['statusId'] == 10))
        ),
        'not-in-service': JSON.parse(
          JSON.stringify(devicesFiltered.filter((row) => row['statusId'] == 11))
        ),
        'on-mission': JSON.parse(
          JSON.stringify(devicesFiltered.filter((row) => row['statusId'] == 12))
        ),
      })
    } else {
      deviceRowsTableStateFiltered().set({
        available: JSON.parse(
          JSON.stringify(allDevices.filter((row) => row['statusId'] == 1))
        ),
        dispatched: JSON.parse(
          JSON.stringify(
            allDevices.filter(
              (row) => row['statusId'] >= 4 && row['statusId'] <= 9
            )
          )
        ),
        disconnected: JSON.parse(
          JSON.stringify(allDevices.filter((row) => row['statusId'] == 10))
        ),
        'not-in-service': JSON.parse(
          JSON.stringify(allDevices.filter((row) => row['statusId'] == 11))
        ),
        'on-mission': JSON.parse(
          JSON.stringify(allDevices.filter((row) => row['statusId'] == 12))
        ),
      })
    }

    return () => {
      clearInterval(freezeMap)
    }
  }, [
    searchDevice.get(),
    searchTask.get(),
    loading.get(),
    triggerNewSocketState().get(),
  ])

  useEffect(() => {
    if (!rowsSelected?.get().length) {
      setRowSelectedModel([])
    }
  }, [rowsSelected?.get().length])

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleReturnToServiceVehicle = async () => {
    await returnToServiceDevice()
      .then((response) => {
        if (response?.status === 200) {
          notify({
            message: translate(response?.data.message),
            type: 'success',
          })
        }
      })
      .finally(() => {
        openReturnToServiceDialog.set(false)
        deviceId.set(null)
      })
  }

  const handleDisconnectDevice = async () => {
    await disconnectDevice()
      .then((response) => {
        if (response?.status === 200) {
          notify({
            message: translate(response?.data.message),
            type: 'success',
          })
        }
      })
      .finally(() => {
        openDisconnectDialog.set(false)
        deviceId.set(null)
        loading.set(false)
      })
  }

  const handleBackToAvailableDevice = async () => {
    await setBackToAvailable()
      .then((response) => {
        if (response?.status === 200) {
          notify({
            message: translate(response?.data.message),
            type: 'success',
          })
        }
      })
      .finally(() => {
        openBackToAvailable.set(false)
        deviceId.set(null)
        loading.set(false)
      })
  }

  const handleClickAction = (params) => {
    const paramDispatchId: number | null = params.row.dispatchId
      ? params.row.dispatchId
      : null
    syncDispatchFromId(paramDispatchId)
  }

  const onRowsSelectionHandler = (ids) => {
    if (!ids.length) {
      rowsSelected?.set([])
    }

    if (enableCheckbox && ids.length) {
      setRowSelectedModel(ids)
      const selectedRowsData = ids.map((id) =>
        JSON.parse(
          JSON.stringify(deviceRowsTableState().get()[typeStatus])
        ).find((row) => row.id === id)
      )
      rowsSelected ? rowsSelected?.set(selectedRowsData) : false
    }
  }

  function DraggableNameCell({ row }) {
    const {
      attributes,
      listeners,
      setNodeRef: setDragRef,
      transform,
      isDragging
    } = useDraggable({
      id: `device-${row.id}`,
      data: { device: row },
    });

    const { setNodeRef: setDropRef, isOver } = useDroppable({
      id: `device-drop-${row.id}`,
      data: { device: row },
    });

    const combinedRef = (node: HTMLDivElement | null) => {
      setDragRef(node);
      setDropRef(node);
    };

    const style: React.CSSProperties = {
      transform: transform
        ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
        : undefined,
      opacity: isDragging ? 0.5 : 1,
      cursor: isDragging ? 'grabbing' : 'grab',
      zIndex: isDragging ? 1000 : 'auto',
      position: isDragging ? 'relative' : 'static',
      padding: isOver ? '4px' : '0',
      borderRadius: isOver ? '4px' : '0',
      transition: 'background-color 0.2s ease, padding 0.2s ease',
    };

    return (
      <div
        ref={combinedRef}
        style={style}
        {...listeners}
        {...attributes}
      >
        <Chip
          icon={<DragIndicatorIcon />}
          sx={{
            '.MuiChip-icon': {
              color: 'white'
            },
            backgroundColor: getDeviceStatusColors(row?.statusId).main,
            color: getDeviceStatusColors(row?.statusId).contrastText,
            '&:hover': {
              backgroundColor: getDeviceStatusColors(row?.statusId).dark,
              color: getDeviceStatusColors(row?.statusId).contrastText,
            },
          }}
          label={row.identification}
        />
      </div>
    );
  }

  const columns: readonly GridColDef[] = [
    { field: 'id' },
    { field: 'agencyId' },
    { field: 'removalRequested' },
    {
      field: 'name',
      headerName: translate('Identification'),
      flex: 1,
      renderCell: (params) => {
        return (
          typeStatus === 'available' && !fullList ? (
            <DraggableNameCell row={params.row} />
          ) : (
            <Chip
              sx={{
                backgroundColor: getDeviceStatusColors(params.row.statusId).main,
                color: getDeviceStatusColors(params.row.statusId).contrastText,
                '&:hover': {
                  backgroundColor: getDeviceStatusColors(params.row.statusId)
                    .dark,
                  color: getDeviceStatusColors(params.row.statusId).contrastText,
                },
              }}
              label={params.row.identification}
            />
          )
        )
      },
    },
    {
      field: 'commander',
      headerName: translate('Commander'),
      flex: 1.5,
      renderCell: (params) => params.row.commander,
    },
    {
      field: 'phone',
      headerName: translate('Phone'),
      flex: 0.7,
      renderCell: (params) => params.row.phone,
    },
    { field: 'task', headerName: translate('Task'), flex: 1.5 },
    {
      field: 'notes',
      headerName: translate('Notes'),
      flex: 1,
      renderCell: (params) => params.row.notes,
    },
    {
      field: 'dispatchGroup',
      headerName: translate('Dispatch Group'),
      flex: 1,
      renderCell: (params) => params.row.dispatchGroup,
    },
    {
      field: 'type',
      headerName: translate('Type'),
      flex: 1,
      renderCell: (params) => params.row.type,
    },
    {
      field: 'subtype',
      headerName: translate('Subtype'),
      flex: 1,
      renderCell: (params) => params.row.subtype,
    },
    {
      field: 'statusChangedAt',
      headerName: translate('Latest status update'),
      flex: 1,
      align: 'center',
      renderCell: (params) => (
        <Tooltip
          title={`Status atualizado em: ${dateTimeFormat(
            params.row.statusChangedAt
          )}`}
          placement="top"
        >
          <Chip
            variant={'outlined'}
            label={elapsedTime(params.row.statusChangedAt)}
          />
        </Tooltip>
      ),
    },
    {
      field: 'deviceStatusId',
      headerName: translate('Status'),
      flex: 0.6,
      align: 'center',
      renderCell: (params) => (
        <Button
          type="submit"
          variant="contained"
          sx={{
            backgroundColor: getDeviceStatusColors(params.row.statusId).main,
            color: getDeviceStatusColors(params.row.statusId).contrastText,
            '&:hover': {
              backgroundColor: getDeviceStatusColors(params.row.statusId).dark,
              color: getDeviceStatusColors(params.row.statusId).contrastText,
            },
          }}
        >
          {params.row.status}
        </Button>
      ),
    },
    {
      field: 'outOfServiceMotive',
      headerName: translate('Out Of Service Reason'),
      flex: 1,
      renderCell: (params) => params.row.outOfServiceReason,
    },
    {
      field: 'onMissionReason',
      headerName: translate('Reason'),
      flex: 1,
      renderCell: (params) => params.row.onMissionReason,
    },
    {
      field: 'outOfServiceDate',
      headerName: translate('Out Of Service Date'),
      flex: 1,
      align: 'center',
      renderCell: (params) => (
        <Tooltip
          title={`Fora de Serviço em: ${dateTimeFormat(
            params.row.outOfServiceDate
          )}`}
          placement="top"
        >
          <Chip
            variant={'outlined'}
            label={elapsedTime(params.row.outOfServiceDate)}
          />
        </Tooltip>
      ),
    },
    {
      field: 'availableDate',
      headerName: translate('Connection Date'),
      flex: 1,
      align: 'center',
      renderCell: (params) => (
        <Tooltip
          title={`Conectada em: ${dateTimeFormat(params.row.availableDate)}`}
          placement="top"
        >
          <Chip
            variant={'outlined'}
            label={elapsedTime(params.row.availableDate)}
          />
        </Tooltip>
      ),
    },
    {
      field: 'disconnectedDate',
      headerName: translate('Disconnection Date'),
      flex: 1,
      align: 'center',
      renderCell: (params) => (
        <Tooltip
          title={
            params.row.disconnectedDate
              ? `Desconectada em: ${dateTimeFormat(
                params.row.disconnectedDate
              )}`
              : translate('This vehicle has never been connected')
          }
          placement="top"
        >
          <Chip
            variant={'outlined'}
            label={
              params.row.disconnectedDate
                ? elapsedTime(params.row.disconnectedDate)
                : translate('Never connected')
            }
          />
        </Tooltip>
      ),
    },
    {
      field: 'onMissionDate',
      headerName: translate('On Mission Date'),
      flex: 1,
      align: 'center',
      renderCell: (params) => (
        <Tooltip
          title={`Em Missão em: ${dateTimeFormat(params.row.onMissionDate)}`}
          placement="top"
        >
          <Chip
            variant={'outlined'}
            label={
              params.row.onMissionDate
                ? elapsedTime(params.row.onMissionDate)
                : translate('Never connected')
            }
          />
        </Tooltip>
      ),
    },

    {
      field: 'statusId',
      headerName: translate('Status ID'),
      flex: 1,
      renderCell: (params) => params.row.statusId,
    },
    {
      field: 'trackingId',
      renderCell: (params) => params.row.trackingId,
    },
    { field: 'dispatchId', headerName: translate('Dispatch'), flex: 1 },
    {
      field: 'dispatchStatus',
      headerName: translate('Dispatch Status'),
      flex: 1,
    },
    {
      field: 'dispatch',
      headerName: translate('Dispatch'),
      flex: 1,
      align: 'center',
      renderCell: (params) => {
        if (params.row.dispatchId) {
          return (
            <Button
              type="submit"
              variant="contained"
              onClick={() => handleClickAction(params)}
              sx={{
                backgroundColor: getStatusColors(params.row.dispatchStatus)
                  .main,
                color: getStatusColors(params.row.dispatchStatus).contrastText,
                '&:hover': {
                  backgroundColor: getStatusColors(params.row.dispatchStatus)
                    .dark,
                  color: getStatusColors(params.row.dispatchStatus)
                    .contrastText,
                },
              }}
            >
              {params.value}
            </Button>
          )
        }
        return '-'
      },
    },
    { field: 'comment', headerName: translate('Comment'), flex: 1 },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      flex: 0.2,
      getActions: ({ id, row }) => [
        <GridActionsCellItem
          key={id}
          icon={<MoreVertIcon />}
          label="export"
          onClick={(event) => {
            setAnchorEl(event?.currentTarget ?? null)
            handleMenuActions(row)
          }}
        />,
      ],
    },
  ]

  const filterColumns = () => {
    const newColumn: GridColDef[] = []
    columns.map((value) => {
      if (!exceptColumns.includes(value.field)) {
        newColumn.push(value)
      }
    })
    return newColumn
  }

  const flashes = keyframes`
      0% { background: ${darkMode ? '#121212' : '#FFF'}}
      100% {background: #ffa726}
    `

  const flashesBK = keyframes`
      0% { background: ${darkMode ? '#121212' : '#FFF'}}
      100% {background: #BFBFBF}
    `

  return (
    <>
      <DataGrid
        sx={{
          height:
            JSON.parse(
              JSON.stringify(deviceRowsTableStateFiltered().get()[typeStatus])
            ).length > 0
              ? 'auto'
              : 300,
          [`.${gridClasses.columnHeaderTitle}`]: {
            fontWeight: 'bold !important',
          },
          cursor: 'pointer',
          '& .flashesRows': {
            background: '#ffa726',
            border: '1px solid #fff',
            animationIterationCount: '4',
            animationTimingFunction: 'ease',
            animationDuration: '2.5s',
            animationName: `${flashes}`,
          },
          '& .closureRequestedFlashesRows': {
            background: '#BFBFBF',
            border: '1px solid #fff',
            animationIterationCount: '4',
            animationTimingFunction: 'ease',
            animationDuration: '2.5s',
            animationName: `${flashesBK}`,
          },
        }}
        loading={loading.get() ? loading.get() : false}
        slots={{
          loadingOverlay: LinearProgress as GridSlots['loadingOverlay'],
          noRowsOverlay: NoRowsOverlay,
        }}
        autoHeight
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        columns={filterColumns()}
        rows={JSON.parse(
          JSON.stringify(deviceRowsTableStateFiltered().get()[typeStatus])
        )}
        paginationMode={'client'}
        pageSizeOptions={[]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: fullList ? 50 : 15,
            },
          },
          sorting: {
            sortModel: [
              {
                field:
                  typeStatus === 'available'
                    ? 'availableDate'
                    : typeStatus === 'dispatched'
                      ? 'statusChangedAt'
                      : typeStatus === 'disconnected'
                        ? 'disconnectedDate'
                        : typeStatus === 'not-in-service'
                          ? 'outOfServiceDate'
                          : typeStatus === 'on-mission'
                            ? 'onMissionDate'
                            : '',
                sort: 'desc',
              },
            ],
          },
        }}
        getRowClassName={({ row }) => {
          return typeStatus === 'dispatched'
            ? row.removalRequested
              ? 'closureRequestedFlashesRows'
              : ''
            : ''
        }}
        columnVisibilityModel={{
          id: false,
          agencyId: false,
          statusId: false,
          dispatchId: false,
          dispatchStatus: false,
          removalRequested: false,
          occurrence_id: false,
        }}
        getRowId={(row) => Number(row.id)}
        checkboxSelection={enableCheckbox}
        onRowSelectionModelChange={(ids) => {
          onRowsSelectionHandler(ids)
        }}
        rowSelectionModel={rowSelectedModel}
        keepNonExistentRowsSelected
        disableColumnSelector
        disableRowSelectionOnClick
        disableColumnMenu
      />
      <MenuList
        anchorEl={anchorEl}
        handleClose={handleClose}
        id={id}
        typeStatus={typeStatus}
      />
      <ConectDispatchVehicle
        lockTemporaryTracking={lockTemporaryTracking.get()}
        openState={openConectDispatchDevice}
        identification={identification.get()}
        deviceId={deviceId}
        plate={plate.get()}
        labelCommander={labelCommander.get()}
        labelDriver={labelDriver.get()}
        labelPatrolmans={labelPatrolmans.get()}
        labelKmInitial={labelKmInitial.get()}
        trackingId={trackingId.get()}
      />
      {typeStatus == 'disconnected' || typeStatus == 'available' ? (
        <DeviceTransferDialog
          title={translate('Transfer Device')}
          deviceId={deviceId.get()}
          deviceName={identification.get()}
          isOpen={openTransferDevice}
          agencyId={agencyId.get()}
          dispatchGroup={dispatchGroup.get()}
          fields={[
            {
              name: translate('To Be Return At'),
              model: 'to_be_returned_at',
              type: 'date',
            },
            { name: translate('Destiny Name'), model: 'destiny_name' },
            {
              name: translate('Destiny Group'),
              model: 'dest_dispatch_group_id',
              type: 'select',
              required: true,
            },
          ]}
        />
      ) : (
        false
      )}
      <DisplayDispatchVehicle
        lockTemporaryTracking={lockTemporaryTracking.get()}
        deviceId={deviceId}
        plate={plate.get()}
        open={openCrewDialog}
        deviceCode={identification}
        canEditCrew={canEditCrew.get()}
        labelCommander={labelCommander.get()}
        labelDriver={labelDriver.get()}
        labelPatrolmans={labelPatrolmans.get()}
        labelKmInitial={labelKmInitial.get()}
        loadingRows={loading}
      />
      <ChronologyDeviceLastDispatchDialog
        id={deviceId.get()}
        open={openChronologyDevice}
      />
      <OutOfServiceDispatchVehicle
        openState={openOutOfServiceDialog}
        identification={identification.get()}
        deviceId={deviceId.get()}
        loadingRows={loading}
      />
      <ReturnToServiceDispatchVehicle
        openState={openReturnToServiceDialog}
        handleAgree={handleReturnToServiceVehicle}
        handleClose={() => {
          openReturnToServiceDialog.set(false)
        }}
        isLoading={loading}
      />
      <DisconnectDispatchVehicle
        identification={identification.get()}
        openState={openDisconnectDialog}
        handleAgree={handleDisconnectDevice}
        handleClose={() => {
          openDisconnectDialog.set(false)
        }}
        isLoading={loading}
        disabled={!deviceId.get()}
      />
      <OnMissionDevice
        deviceId={deviceId.get()}
        openState={openOnMission}
        identification={identification.get()}
      />

      <BackToAvailable
        openState={openBackToAvailable}
        handleAgree={handleBackToAvailableDevice}
        handleClose={() => {
          openBackToAvailable.set(false)
        }}
        isLoading={loading}
      />
    </>
  )
}
